import { observer } from 'mobx-react-lite'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { useInjection } from '@core/providers/inversify-react.provider'
import { NotificationPresenter, type NotificationType } from './notifications.presenter'

// Literal colors, not shell-* tokens — these carry meaning (success/error/...),
// not decoration, so they stay fixed across light/dark rather than re-theming.
const ICON: Record<NotificationType, { Icon: typeof CheckCircleIcon; className: string }> = {
  success: { Icon: CheckCircleIcon, className: 'text-green-500' },
  error: { Icon: XCircleIcon, className: 'text-red-500' },
  warning: { Icon: ExclamationTriangleIcon, className: 'text-amber-500' },
  info: { Icon: InformationCircleIcon, className: 'text-shell-accent' },
}

export const NotificationsComponent = observer(() => {
  const presenter: NotificationPresenter = useInjection(NotificationPresenter)
  const vm = presenter.viewModel

  if (!vm.showNotifications) return null

  // No own fixed positioning — TopRightStackComponent (mounted once) provides
  // the single fixed top-right container this and FeedbackComponent stack
  // inside, so both showing at once don't overlap each other.
  return (
    <>
      {vm.notifications.map((notification) => {
        const { Icon, className } = ICON[notification.type]
        return (
          <div
            key={notification.id}
            role="alert"
            className="flex items-start gap-x-2 rounded-md border border-shell-border bg-shell-surface p-3 shadow-lg"
          >
            <Icon className={`size-5 shrink-0 ${className}`} aria-hidden="true" />
            <p className="flex-1 text-sm text-shell-ink">{notification.message}</p>
            <button
              type="button"
              onClick={() => presenter.dismiss(notification.id)}
              aria-label={vm.dismissLabel}
              className="shrink-0 rounded-md p-0.5 text-shell-ink-muted hover:text-shell-ink"
            >
              <XMarkIcon className="size-4" aria-hidden="true" />
            </button>
          </div>
        )
      })}
    </>
  )
})
